import { produce } from 'immer';
import type {
  ReservationSummaryResponse,
  ReservationCheckinKpiResponse,
  ReservationLogItem,
  ReservationDetailResponse,
  ReservationByStatusItem,
  ReservationHourlyItem,
  TodayNewDetailsItem,
} from '../../../interface/reservationReportInterface';
import {
  FETCH_RESERVATION_SUMMARY_REQUEST,
  FETCH_RESERVATION_SUMMARY_SUCCESS,
  FETCH_RESERVATION_SUMMARY_FAILURE,
  FETCH_RESERVATION_CHECKIN_KPI_REQUEST,
  FETCH_RESERVATION_CHECKIN_KPI_SUCCESS,
  FETCH_RESERVATION_CHECKIN_KPI_FAILURE,
  FETCH_RESERVATION_LOG_REQUEST,
  FETCH_RESERVATION_LOG_SUCCESS,
  FETCH_RESERVATION_LOG_FAILURE,
  FETCH_RESERVATION_DETAIL_REQUEST,
  FETCH_RESERVATION_DETAIL_SUCCESS,
  FETCH_RESERVATION_DETAIL_FAILURE,
  CLEAR_RESERVATION_DETAIL,
  FETCH_RESERVATION_BY_STATUS_REQUEST,
  FETCH_RESERVATION_BY_STATUS_SUCCESS,
  FETCH_RESERVATION_BY_STATUS_FAILURE,
  FETCH_RESERVATION_HOURLY_REQUEST,
  FETCH_RESERVATION_HOURLY_SUCCESS,
  FETCH_RESERVATION_HOURLY_FAILURE,
  FETCH_TODAY_NEW_DETAILS_REQUEST,
  FETCH_TODAY_NEW_DETAILS_SUCCESS,
  FETCH_TODAY_NEW_DETAILS_FAILURE,
} from './reservationReportConstants';

const initialState: {
  // Summary KPIs
  reservationSummaryLoading: boolean;
  reservationSummary: ReservationSummaryResponse | null;
  reservationSummaryFailure: boolean;
  // Checkin KPIs
  checkinKpiLoading: boolean;
  checkinKpi: ReservationCheckinKpiResponse | null;
  checkinKpiFailure: boolean;
  // Reservation Log
  reservationLogLoading: boolean;
  reservationLog: ReservationLogItem[];
  reservationLogTotalElements: number;
  reservationLogTotalPages: number;
  reservationLogFailure: boolean;
  // Reservation Detail
  reservationDetailLoading: boolean;
  reservationDetail: ReservationDetailResponse | null;
  reservationDetailFailure: boolean;
  // By Status
  reservationByStatusLoading: boolean;
  reservationByStatus: ReservationByStatusItem[];
  reservationByStatusFailure: boolean;
  // Hourly
  reservationHourlyLoading: boolean;
  reservationHourly: ReservationHourlyItem[];
  reservationHourlyFailure: boolean;
  // Today New Details
  todayNewDetailsLoading: boolean;
  todayNewDetails: TodayNewDetailsItem[];
  todayNewDetailsTotalElements: number;
  todayNewDetailsTotalPages: number;
  todayNewDetailsFailure: boolean;
} = {
  reservationSummaryLoading: false,
  reservationSummary: null,
  reservationSummaryFailure: false,

  checkinKpiLoading: false,
  checkinKpi: null,
  checkinKpiFailure: false,

  reservationLogLoading: false,
  reservationLog: [],
  reservationLogTotalElements: 0,
  reservationLogTotalPages: 1,
  reservationLogFailure: false,

  reservationDetailLoading: false,
  reservationDetail: null,
  reservationDetailFailure: false,

  reservationByStatusLoading: false,
  reservationByStatus: [],
  reservationByStatusFailure: false,

  reservationHourlyLoading: false,
  reservationHourly: [],
  reservationHourlyFailure: false,

  todayNewDetailsLoading: false,
  todayNewDetails: [],
  todayNewDetailsTotalElements: 0,
  todayNewDetailsTotalPages: 1,
  todayNewDetailsFailure: false,
};

export default function reservationReportReducer(state = initialState, action: any) {
  return produce(state, (draft) => {
    switch (action.type) {
      case FETCH_RESERVATION_SUMMARY_REQUEST:
        draft.reservationSummaryLoading = true;
        draft.reservationSummary = null;
        draft.reservationSummaryFailure = false;
        break;
      case FETCH_RESERVATION_SUMMARY_SUCCESS:
        draft.reservationSummaryLoading = false;
        draft.reservationSummary = action.payload;
        draft.reservationSummaryFailure = false;
        break;
      case FETCH_RESERVATION_SUMMARY_FAILURE:
        draft.reservationSummaryLoading = false;
        draft.reservationSummary = null;
        draft.reservationSummaryFailure = true;
        break;

      case FETCH_RESERVATION_CHECKIN_KPI_REQUEST:
        draft.checkinKpiLoading = true;
        draft.checkinKpi = null;
        draft.checkinKpiFailure = false;
        break;
      case FETCH_RESERVATION_CHECKIN_KPI_SUCCESS:
        draft.checkinKpiLoading = false;
        draft.checkinKpi = action.payload;
        draft.checkinKpiFailure = false;
        break;
      case FETCH_RESERVATION_CHECKIN_KPI_FAILURE:
        draft.checkinKpiLoading = false;
        draft.checkinKpi = null;
        draft.checkinKpiFailure = true;
        break;

      case FETCH_RESERVATION_LOG_REQUEST:
        draft.reservationLogLoading = true;
        draft.reservationLog = [];
        draft.reservationLogFailure = false;
        break;
      case FETCH_RESERVATION_LOG_SUCCESS:
        draft.reservationLogLoading = false;
        draft.reservationLog = action.payload.reservations ?? [];
        draft.reservationLogTotalElements = Number(action.payload.totalElements) || 0;
        draft.reservationLogTotalPages = Number(action.payload.totalPages) || 1;
        draft.reservationLogFailure = false;
        break;
      case FETCH_RESERVATION_LOG_FAILURE:
        draft.reservationLogLoading = false;
        draft.reservationLog = [];
        draft.reservationLogTotalElements = 0;
        draft.reservationLogTotalPages = 1;
        draft.reservationLogFailure = true;
        break;

      case FETCH_RESERVATION_DETAIL_REQUEST:
        draft.reservationDetailLoading = true;
        draft.reservationDetail = null;
        draft.reservationDetailFailure = false;
        break;
      case FETCH_RESERVATION_DETAIL_SUCCESS:
        draft.reservationDetailLoading = false;
        draft.reservationDetail = action.payload;
        draft.reservationDetailFailure = false;
        break;
      case FETCH_RESERVATION_DETAIL_FAILURE:
        draft.reservationDetailLoading = false;
        draft.reservationDetail = null;
        draft.reservationDetailFailure = true;
        break;
      case CLEAR_RESERVATION_DETAIL:
        draft.reservationDetailLoading = false;
        draft.reservationDetail = null;
        draft.reservationDetailFailure = false;
        break;

      case FETCH_RESERVATION_BY_STATUS_REQUEST:
        draft.reservationByStatusLoading = true;
        draft.reservationByStatus = [];
        draft.reservationByStatusFailure = false;
        break;
      case FETCH_RESERVATION_BY_STATUS_SUCCESS:
        draft.reservationByStatusLoading = false;
        draft.reservationByStatus = action.payload ?? [];
        draft.reservationByStatusFailure = false;
        break;
      case FETCH_RESERVATION_BY_STATUS_FAILURE:
        draft.reservationByStatusLoading = false;
        draft.reservationByStatus = [];
        draft.reservationByStatusFailure = true;
        break;

      case FETCH_RESERVATION_HOURLY_REQUEST:
        draft.reservationHourlyLoading = true;
        draft.reservationHourly = [];
        draft.reservationHourlyFailure = false;
        break;
      case FETCH_RESERVATION_HOURLY_SUCCESS:
        draft.reservationHourlyLoading = false;
        draft.reservationHourly = action.payload ?? [];
        draft.reservationHourlyFailure = false;
        break;
      case FETCH_RESERVATION_HOURLY_FAILURE:
        draft.reservationHourlyLoading = false;
        draft.reservationHourly = [];
        draft.reservationHourlyFailure = true;
        break;

      case FETCH_TODAY_NEW_DETAILS_REQUEST:
        draft.todayNewDetailsLoading = true;
        draft.todayNewDetails = [];
        draft.todayNewDetailsFailure = false;
        break;
      case FETCH_TODAY_NEW_DETAILS_SUCCESS: {
        const raw = action.payload?.metaDataInfo?.data ?? action.payload;
        draft.todayNewDetailsLoading = false;
        draft.todayNewDetails = raw?.content ?? [];
        draft.todayNewDetailsTotalElements = Number(raw?.totalElements) || 0;
        draft.todayNewDetailsTotalPages = Number(raw?.totalPages) || 1;
        draft.todayNewDetailsFailure = false;
        break;
      }
      case FETCH_TODAY_NEW_DETAILS_FAILURE:
        draft.todayNewDetailsLoading = false;
        draft.todayNewDetails = [];
        draft.todayNewDetailsTotalElements = 0;
        draft.todayNewDetailsTotalPages = 1;
        draft.todayNewDetailsFailure = true;
        break;

      default:
        return state;
    }
  });
}
